import Button from "components/Button";
import ConsentDisclosure from "components/ConsentDisclosure";
import NoticeBanner from "components/NoticeBanner";
import IdentifierForm from "pages/Login/IdentifierForm";
import {
  sendButtonLabel,
  type GrantMethod,
} from "pages/Login/login-grant-flow";

import {
  ALTERNATIVE_EMAIL_LABEL,
  alternativeMethodOf,
  destinationFor,
  leadingMethodOf,
  reentryDisclosure,
  sendLabelFor,
  type ReentryOptions,
} from "./reentry";

function Disclosure({
  method,
  options,
  typed,
}: {
  method: GrantMethod | null;
  options: ReentryOptions;
  typed: boolean;
}) {
  return (
    <p className="text-left text-sm leading-normal text-oat-700">
      <ConsentDisclosure
        disclosure={reentryDisclosure({
          method,
          destination: typed ? "typed" : "on_file",
          hasAccount: !typed && options.hasAccount,
          maskedPhone: options.sms,
        })}
      />
    </p>
  );
}

function MethodButton({
  method,
  options,
  label,
  variant,
  busy,
  onChoose,
}: {
  method: GrantMethod;
  options: ReentryOptions;
  label: string;
  variant: "continue" | "outline";
  busy: GrantMethod | null;
  onChoose: (method: GrantMethod) => void;
}) {
  const destinationId = `reentry-${method}-destination`;
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant={variant}
        fullWidth
        disabled={busy !== null}
        isLoading={busy === method}
        loadingText={sendButtonLabel(label, busy)}
        ariaDescribedBy={destinationId}
        onClick={() => onChoose(method)}
      >
        {label}
      </Button>
      <p
        id={destinationId}
        className="ph-no-capture break-words text-sm text-oat-700"
      >
        {destinationFor(options, method)}
      </p>
      <Disclosure method={method} options={options} typed={false} />
    </div>
  );
}

/** The re-entry choices for a link whose recipient the server knows: the
 * leading channel as the primary button, the other, when there is one,
 * beside it. Each shows its masked destination and its own disclosure. */
export function OnFileChoice({
  options,
  busy,
  error,
  onChoose,
}: {
  options: ReentryOptions;
  busy: GrantMethod | null;
  error: string | null;
  onChoose: (method: GrantMethod) => void;
}) {
  const leading = leadingMethodOf(options);
  const alternative = alternativeMethodOf(options);
  if (leading === null) return null;
  return (
    <div className="flex w-full flex-col gap-5">
      {error && <NoticeBanner tone="error">{error}</NoticeBanner>}
      <MethodButton
        method={leading}
        options={options}
        label={sendLabelFor(leading)}
        variant="continue"
        busy={busy}
        onChoose={onChoose}
      />
      {alternative && (
        <MethodButton
          method={alternative}
          options={options}
          label={ALTERNATIVE_EMAIL_LABEL}
          variant="outline"
          busy={busy}
          onChoose={onChoose}
        />
      )}
    </div>
  );
}

/** The one field for a visitor with no recipient on file: the button says
 * what will be sent to what they typed. */
export function TypedForm({
  notice,
  initialValue,
  busy,
  onSubmit,
}: {
  notice: string | null;
  /** What to start the field with, such as what was typed before. */
  initialValue?: string;
  busy: boolean;
  onSubmit: (identifier: string) => void;
}) {
  return (
    <IdentifierForm
      id="reentry-identifier"
      notice={notice}
      initialValue={initialValue}
      busy={busy}
      onSubmit={onSubmit}
      buttonLabel={sendLabelFor}
      loadingLabel={(method) => sendButtonLabel(sendLabelFor(method), method)}
      renderNote={(method) => (
        <Disclosure
          method={method}
          options={{ sms: null, email: null, hasAccount: false }}
          typed
        />
      )}
    />
  );
}
